// frontend/src/App.tsx

import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

// Customer Pages
import HomePage from './pages/customer/HomePage';

// Auth Pages

// Admin Pages

// Admin Pages - Referral System

// Admin Pages - Coupons

// Admin Pages - Orders Sub-pages

// Admin Pages - Track Order

// Admin Order Create Page

// Employee Pages

// Components & Layouts
import PrivateRoute from './components/common/PrivateRoute';
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import FloatingCartBar from './components/common/FloatingCartBar';
import AdminLayout from './components/layouts/AdminLayout';
import EmployeeLayout from './components/layouts/EmployeeLayout';
import NavigationLoader from './components/common/NavigationLoader';

// ---- Lazy-loaded pages (code splitting) ----
const ProductsPage = lazy(() => import('./pages/customer/ProductsPage'));
const ProductDetailPage = lazy(() => import('./pages/customer/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/customer/CartPage'));
const CheckoutPage = lazy(() => import('./pages/customer/CheckoutPage'));
const OrdersPage = lazy(() => import('./pages/customer/OrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/customer/OrderDetailPage'));
const Wishlist = lazy(() => import('./pages/customer/Wishlist'));
const AboutPage = lazy(() => import('./pages/customer/AboutPage'));
const ProfilePage = lazy(() => import('./pages/customer/ProfilePage'));
const ContactPage = lazy(() => import('./pages/customer/ContactPage'));
const FaqPage = lazy(() => import('./pages/customer/FaqPage'));
const ShippingInfoPage = lazy(() => import('./pages/customer/ShippingInfoPage'));
const ReturnsPolicyPage = lazy(() => import('./pages/customer/ReturnsPolicyPage'));
const RefundPolicyPage = lazy(() => import('./pages/customer/RefundPolicyPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/customer/PrivacyPolicyPage'));
const TermsAndConditionsPage = lazy(() => import('./pages/customer/TermsAndConditionsPage'));
const CategoriesPage = lazy(() => import('./pages/customer/CategoriesPage'));
const ShopByAgePage = lazy(() => import('./pages/customer/ShopByAgePage'));
const TrendingPage = lazy(() => import('./pages/customer/TrendingPage'));
const FestivePage = lazy(() => import('./pages/customer/Festivepage'));
const WalletPage = lazy(() => import('./pages/customer/WalletPage'));
const Offers = lazy(() => import('./pages/customer/Offers'));
const PaymentResultPage = lazy(() => import('./pages/customer/PaymentResultPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminProducts = lazy(() => import('./pages/admin/AdminProducts'));
const AdminProductDetailPage = lazy(() => import('./pages/admin/AdminProductDetail'));
const AdminProductShare = lazy(() => import('./pages/admin/AdminProductShare'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminOrderDetail = lazy(() => import('./pages/admin/OrderDetail'));
const AdminEmployees = lazy(() => import('./pages/admin/AdminEmployees'));
const EmployeeDetails = lazy(() => import('./pages/admin/EmployeeDetails'));
const EmployeePermissions = lazy(() => import('./pages/admin/EmployeePermissions'));
const AdminCommissions = lazy(() => import('./pages/admin/AdminCommissions'));
const AdminReports = lazy(() => import('./pages/admin/AdminReports'));
const AdminCategoriesPage = lazy(() => import('./pages/admin/CategoriesPage'));
const AgeGroupsPage = lazy(() => import('./pages/admin/AgeGroups'));
const AdminSliderPage = lazy(() => import('./pages/admin/AdminSliderPage'));
const AdminAboutPage = lazy(() => import('./pages/admin/AdminAboutPage'));
const InventoryPurchasesPage = lazy(() => import('./pages/admin/InventoryPurchases'));
const AdminContactPage = lazy(() => import('./pages/admin/ContactUsAdmin'));
const AdminReviews = lazy(() => import('./pages/admin/AdminReviews'));
const Warehouse = lazy(() => import('./pages/admin/Warehouse'));
const AnnouncementsAdmin = lazy(() => import('./pages/admin/Announcementsadmin'));
const PaymentChargesSettings = lazy(() => import('./pages/admin/Paymentchargessettings'));
const CodConfiguration = lazy(() => import('./pages/admin/CodConfiguration'));
const Customers = lazy(() => import('./pages/admin/Customers'));
const AdminWallet = lazy(() => import('./pages/admin/AdminWallet'));
const AdminWithdrawals = lazy(() => import('./pages/admin/AdminWithdrawals'));
const AdminReferralSettings = lazy(() => import('./pages/admin/AdminReferralSettings'));
const FreeGiftCoupon = lazy(() => import('./pages/admin/FreeGiftCoupon'));
const BXGYCoupon = lazy(() => import('./pages/admin/BXGYCoupon'));
const GenericCoupon = lazy(() => import('./pages/admin/GenericCoupon'));
const PostCheckoutOffer = lazy(() => import('./pages/admin/PostCheckoutOffer'));
const RepeatUserMarketing = lazy(() => import('./pages/admin/RepeatUserMarketing'));
const ParticularCustomerCoupon = lazy(() => import('./pages/admin/ParticularCustomerCoupon'));
const ReturnExchangeDashboard = lazy(() => import('./pages/admin/Returnexchangedashboard'));
const AdminReplacements = lazy(() => import('./pages/admin/AdminReplacements'));
const AdminNDR = lazy(() => import('./pages/admin/AdminNDR'));
const AdminWhatsAppRetarget = lazy(() => import('./pages/admin/AdminWhatsAppRetarget'));
const AdminRTO = lazy(() => import('./pages/admin/AdminRTO'));
const AdminCODRemittance = lazy(() => import('./pages/admin/AdminCODRemittance'));
const TrackOrder = lazy(() => import('./pages/admin/TrackOrder'));
const AdminOrderCreate = lazy(() => import('./pages/admin/AdminOrderCreate'));
const EmployeeDashboard = lazy(() => import('./pages/employee/EmployeeDashboard'));
const EmployeeWallet = lazy(() => import('./pages/employee/EmployeeWallet'));
const EmployeeReferrals = lazy(() => import('./pages/employee/EmployeeReferrals'));
const EmployeeProfile = lazy(() => import('./pages/employee/EmployeeProfile'));
const EmployeeOrderDetail = lazy(() => import('./pages/employee/EmployeeOrderDetail'));
const EmployeeAgeGroups = lazy(() => import('./pages/employee/EmployeeAgeGroups'));
const EmployeeCategories = lazy(() => import('./pages/employee/EmployeeCategories'));
const EmployeeProducts = lazy(() => import('./pages/employee/EmployeeProducts'));
const EmployeeSlider = lazy(() => import('./pages/employee/EmployeeSlider'));
const EmployeeEmployees = lazy(() => import('./pages/employee/EmployeeEmployees'));
const EmployeeReferralSettings = lazy(() => import('./pages/employee/EmployeeReferralSettings'));
const EmployeeInventory = lazy(() => import('./pages/employee/EmployeeInventory'));
const EmployeeWithdrawals = lazy(() => import('./pages/employee/EmployeeWithdrawals'));
const EmployeeAboutPage = lazy(() => import('./pages/employee/EmployeeAboutPage'));
const EmployeeContactPage = lazy(() => import('./pages/employee/EmployeeContact'));
const EmployeeCustomers = lazy(() => import('./pages/employee/EmployeeCustomers'));
const EmployeeWarehouse = lazy(() => import('./pages/employee/EmployeeWarehouse'));
const EmployeeProductShare = lazy(() => import('./pages/employee/EmployeeProductShare'));
const EmployeeCodConfiguration = lazy(() => import('./pages/employee/EmployeeCodConfiguration'));
const EmployeeCodRemittance = lazy(() => import('./pages/employee/EmployeeCodRemittance'));

// PageLayout Component
const PageLayout = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-col min-h-screen">
    <Navbar />
    <main className="flex-grow">{children}</main>
    <Footer />
    <FloatingCartBar />
  </div>
);

// Inner app content
const AppContent = () => {
  return (
    <>
      <NavigationLoader />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#363636',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            borderRadius: '8px',
            padding: '12px 16px',
          },
          success: { iconTheme: { primary: '#2D6A4F', secondary: '#fff' } },
          error:   { iconTheme: { primary: '#C27B7F', secondary: '#fff' } },
        }}
      />

      <Suspense
        fallback={
          <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
            <style>{`@keyframes tf-spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 48, height: 48, border: '4px solid #fbcfe8', borderTopColor: '#ec4899', borderRadius: '50%', animation: 'tf-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
              <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Loading...</p>
            </div>
          </div>
        }
      >
        <Routes>

        {/* ==================== PUBLIC ROUTES ==================== */}
        <Route path="/" element={<PageLayout><HomePage /></PageLayout>} />
        <Route path="/products" element={<PageLayout><ProductsPage /></PageLayout>} />
        <Route path="/products/:id" element={<PageLayout><ProductDetailPage /></PageLayout>} />
        <Route path="/categories" element={<PageLayout><CategoriesPage /></PageLayout>} />
        <Route path="/shop-by-age" element={<PageLayout><ShopByAgePage /></PageLayout>} />
        <Route path="/trending" element={<PageLayout><TrendingPage /></PageLayout>} />
        <Route path="/festive" element={<PageLayout><FestivePage /></PageLayout>} />
        <Route path="/login" element={<PageLayout><LoginPage /></PageLayout>} />
        <Route path="/register" element={<PageLayout><RegisterPage /></PageLayout>} />
        <Route path="/about" element={<PageLayout><AboutPage /></PageLayout>} />
        <Route path="/contact" element={<PageLayout><ContactPage /></PageLayout>} />
        <Route path="/faq" element={<PageLayout><FaqPage /></PageLayout>} />
        <Route path="/shipping-info" element={<PageLayout><ShippingInfoPage /></PageLayout>} />
        <Route path="/returns-policy" element={<PageLayout><ReturnsPolicyPage /></PageLayout>} />
        <Route path="/refund-policy" element={<PageLayout><RefundPolicyPage /></PageLayout>} />  {/* ✅ ADDED */}
        <Route path="/privacy-policy" element={<PageLayout><PrivacyPolicyPage /></PageLayout>} />
        <Route path="/terms-of-service" element={<PageLayout><TermsAndConditionsPage /></PageLayout>} />

        {/* CART PAGE - PUBLIC (Guest users can view cart) */}
        <Route path="/cart" element={<PageLayout><CartPage /></PageLayout>} />

        {/* CHECKOUT PAGE - PUBLIC (Guest users can proceed to checkout directly) */}
        <Route path="/checkout" element={<PageLayout><CheckoutPage /></PageLayout>} />
        <Route path="/payment-success" element={<PageLayout><PaymentResultPage type="success" /></PageLayout>} />
        <Route path="/payment-failure" element={<PageLayout><PaymentResultPage type="failure" /></PageLayout>} />

        {/* ==================== CUSTOMER PROTECTED ROUTES ==================== */}
        <Route path="/profile"  element={<PrivateRoute role="customer"><PageLayout><ProfilePage /></PageLayout></PrivateRoute>} />
        <Route path="/wishlist" element={<PrivateRoute role="customer"><PageLayout><Wishlist /></PageLayout></PrivateRoute>} />
        <Route path="/wallet"   element={<PrivateRoute role="customer"><PageLayout><WalletPage /></PageLayout></PrivateRoute>} />
        <Route path="/offers"   element={<PrivateRoute role="customer"><PageLayout><Offers /></PageLayout></PrivateRoute>} />
        <Route path="/orders"      element={<PrivateRoute role="customer"><PageLayout><OrdersPage /></PageLayout></PrivateRoute>} />
        <Route path="/orders/:id"  element={<PrivateRoute role="customer"><PageLayout><OrderDetailPage /></PageLayout></PrivateRoute>} />

        {/* ==================== ADMIN ROUTES ==================== */}
        <Route
          path="/admin"
          element={
            <PrivateRoute role="admin">
              <AdminLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<AdminDashboard />} />

          {/* Categories & Age */}
          <Route path="categories" element={<AdminCategoriesPage />} />
          <Route path="age"        element={<AgeGroupsPage />} />

          {/* Products */}
          <Route path="products"        element={<AdminProducts />} />
          <Route path="products/:id"    element={<AdminProductDetailPage />} />
          <Route path="product-share"   element={<AdminProductShare />} />
          <Route path="whatsapp-retarget" element={<AdminWhatsAppRetarget />} />
          <Route path="reviews"         element={<AdminReviews />} />
          <Route path="inventory"       element={<InventoryPurchasesPage />} />

          {/* Customers */}
          <Route path="customers"       element={<Customers />} />

          {/* Orders & sub-pages */}
          <Route path="orders"                        element={<AdminOrders />} />
          <Route path="orders/create"                 element={<AdminOrderCreate />} />
          <Route path="orders/track"                  element={<TrackOrder />} />
          <Route path="orders/return-exchange"        element={<ReturnExchangeDashboard />} />
          <Route path="orders/replacements"           element={<AdminReplacements />} />
          <Route path="orders/ndr"                    element={<AdminNDR />} />
          <Route path="orders/rto"                    element={<AdminRTO />} />
          <Route path="cod-remittance"                element={<AdminCODRemittance />} />
          <Route path="orders/:orderId"               element={<AdminOrderDetail />} />

          {/* Employees */}
          <Route path="employees"                     element={<AdminEmployees />} />
          <Route path="employees/:id"                 element={<EmployeeDetails />} />
          <Route path="employees/edit/:id"            element={<AdminEmployees />} />
          <Route path="employees/:id/permissions"     element={<EmployeePermissions />} />

          {/* Finance */}
          <Route path="commissions"       element={<AdminCommissions />} />
          <Route path="reports"           element={<AdminReports />} />
          <Route path="wallet"            element={<AdminWallet />} />
          <Route path="withdrawals"       element={<AdminWithdrawals />} />
          <Route path="referral-settings" element={<AdminReferralSettings />} />

          {/* CMS */}
          <Route path="slider"         element={<AdminSliderPage />} />
          <Route path="contact"        element={<AdminContactPage />} />
          <Route path="about"          element={<AdminAboutPage />} />
          <Route path="announcements"  element={<AnnouncementsAdmin />} />

          {/* Warehouse */}
          <Route path="warehouse" element={<Warehouse />} />

          {/* Coupons */}
          <Route path="coupons/free-gift"       element={<FreeGiftCoupon />} />
          <Route path="coupons/bxgy"            element={<BXGYCoupon />} />
          <Route path="coupons/generic"         element={<GenericCoupon />} />
          <Route path="coupons/post-checkout"   element={<PostCheckoutOffer />} />
          <Route path="coupons/repeat-user"     element={<RepeatUserMarketing />} />
          <Route path="coupons/particular-customer" element={<ParticularCustomerCoupon />} />

          {/* Invoice & Charges */}
          <Route path="invoice-charges" element={<PaymentChargesSettings />} />
          <Route path="cod-configuration" element={<CodConfiguration />} />
        </Route>

        {/* ==================== EMPLOYEE ROUTES ==================== */}
        <Route
          path="/employee"
          element={
            <PrivateRoute role="employee">
              <EmployeeLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<EmployeeDashboard />} />
          <Route path="dashboard"  element={<EmployeeDashboard />} />
          <Route path="profile"    element={<EmployeeProfile />} />

          {/* EMPLOYEE ORDERS & NDR ROUTES */}
          <Route path="orders" element={<AdminOrders />} />
          <Route path="ndr" element={<AdminNDR />} />
          <Route path="rto" element={<AdminRTO />} />
          <Route path="returns" element={<ReturnExchangeDashboard />} />
          <Route path="track-orders" element={<TrackOrder />} />
          
          {/* ✅ ADDED: Order Create Route - For Order Again functionality (Employee Access) */}
          <Route path="orders/create" element={<AdminOrderCreate />} />
          
          {/* Order Again - Redirect to employee orders/create */}
          <Route path="order-again" element={<Navigate to="/employee/orders/create" replace />} />

          <Route path="wallet"            element={<EmployeeWallet />} />
          <Route path="withdrawals"       element={<EmployeeWithdrawals />} />
          <Route path="referrals"         element={<EmployeeReferrals />} />
          <Route path="referral-settings" element={<EmployeeReferralSettings />} />

          <Route path="orders/:id" element={<EmployeeOrderDetail />} />

          <Route path="age-groups"          element={<EmployeeAgeGroups />} />
          <Route path="age-groups/add"      element={<EmployeeAgeGroups />} />
          <Route path="age-groups/edit/:id" element={<EmployeeAgeGroups />} />

          <Route path="categories"          element={<EmployeeCategories />} />
          <Route path="categories/add"      element={<EmployeeCategories />} />
          <Route path="categories/edit/:id" element={<EmployeeCategories />} />

          <Route path="products"          element={<EmployeeProducts />} />
          <Route path="products/add"      element={<EmployeeProducts />} />
          <Route path="products/edit/:id" element={<EmployeeProducts />} />

          <Route path="slider"          element={<EmployeeSlider />} />
          <Route path="slider/add"      element={<EmployeeSlider />} />
          <Route path="slider/edit/:id" element={<EmployeeSlider />} />

          <Route path="inventory"          element={<EmployeeInventory />} />
          <Route path="inventory/add"      element={<EmployeeInventory />} />
          <Route path="inventory/edit/:id" element={<EmployeeInventory />} />

          <Route path="customers"                element={<EmployeeCustomers />} />
          <Route path="warehouse"                element={<EmployeeWarehouse />} />
          <Route path="product-share"            element={<EmployeeProductShare />} />
          <Route path="cod-configuration"        element={<EmployeeCodConfiguration />} />
          <Route path="cod-remittance"           element={<EmployeeCodRemittance />} />
          <Route path="employees"                element={<EmployeeEmployees />} />
          <Route path="employees/add"            element={<EmployeeEmployees />} />
          <Route path="employees/:id/edit"       element={<EmployeeEmployees />} />
          <Route path="employees/:id/permissions" element={<EmployeeEmployees />} />

          <Route path="about"          element={<EmployeeAboutPage />} />
          <Route path="about/add"      element={<EmployeeAboutPage />} />
          <Route path="about/edit/:id" element={<EmployeeAboutPage />} />

          <Route path="contact"          element={<EmployeeContactPage />} />
          <Route path="contact/add"      element={<EmployeeContactPage />} />
          <Route path="contact/edit/:id" element={<EmployeeContactPage />} />

          <Route path="settings"         element={<EmployeeProfile />} />
          <Route path="settings/profile" element={<EmployeeProfile />} />
        </Route>

        {/* ==================== 404 CATCH ALL ==================== */}
        <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
      </Suspense>
    </>
  );
};

function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAutoLogin = () => {
      let token    = localStorage.getItem('token');
      let userData = localStorage.getItem('user');
      let storageUsed = 'localStorage';

      if (!token) {
        token       = sessionStorage.getItem('token');
        userData    = sessionStorage.getItem('user');
        storageUsed = 'sessionStorage';
      }

      if (token && userData) {
        try {
          const user = JSON.parse(userData);
          console.log(`✅ Auto-login successful using ${storageUsed} for user:`, user.email || user.name || user.first_name);
        } catch (error) {
          console.error('Failed to parse user data:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('user');
        }
      } else {
        console.log('No saved session found');
      }
      setLoading(false);
    };
    checkAutoLogin();
  }, []);

  if (loading) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        height: '100vh', flexDirection: 'column', gap: '20px', background: '#fff',
      }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            position: 'absolute', width: '90px', height: '90px',
            border: '3px solid #fce7f3', borderTop: '3px solid #ec4899',
            borderRadius: '50%', animation: 'spin 1s linear infinite',
          }} />
          <img
            src="/logo.png" alt="TwinkleFanta"
            style={{ width: '65px', height: '65px', borderRadius: '50%', objectFit: 'cover', animation: 'pulse 1.5s ease-in-out infinite' }}
          />
        </div>
        <p style={{ color: '#ec4899', fontSize: '16px', fontWeight: '600', letterSpacing: '0.5px', animation: 'fadeInOut 1.5s ease-in-out infinite' }}>
          Loading...
        </p>
        <style>{`
          @keyframes spin        { 0% { transform: rotate(0deg); }   100% { transform: rotate(360deg); } }
          @keyframes pulse       { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(0.95); opacity: 0.85; } }
          @keyframes fadeInOut   { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        `}</style>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;